import {
  Button,
  Box,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Input,
  Spinner,
  Textarea,
  Text,
  VStack,
  useColorModeValue as mode,
  useDisclosure,
  useToast,
} from "@chakra-ui/react"
import { InfoOutlineIcon } from "@chakra-ui/icons"
import { ChevronLeftIcon, ChevronRightIcon, WarningIcon } from "@chakra-ui/icons"
import { useCallback, useEffect, useRef, useState } from "react"
import axios from "axios"
import { useSelector } from "react-redux"
import { getApiBaseUrl } from "../utils/axiosConfig"
import { formatAUDate, formatAUTime, getBusinessTimeZone } from "../global-client"
import JobCard from "./JobCard"

const CallerIdLiveListener = () => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const toast = useToast()
  const { userInfo } = useSelector((state) => state.user)
  const [incomingCall, setIncomingCall] = useState(null)
  const [callHistory, setCallHistory] = useState([])
  const [callHistoryIndex, setCallHistoryIndex] = useState(0)
  const [callHistoryLoading, setCallHistoryLoading] = useState(false)
  const [customerJobs, setCustomerJobs] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [isAutoClosePaused, setIsAutoClosePaused] = useState(false)
  const [categoryLoading, setCategoryLoading] = useState(false)
  const [noteLoading, setNoteLoading] = useState(false)
  const [isNoteEditorOpen, setIsNoteEditorOpen] = useState(false)
  const [callerNameDraft, setCallerNameDraft] = useState("")
  const [noteDraft, setNoteDraft] = useState("")
  const [editingNoteId, setEditingNoteId] = useState(null)
  const [draftNotes, setDraftNotes] = useState([])
  const [initialNotes, setInitialNotes] = useState([])
  const [initialCallerName, setInitialCallerName] = useState("")
  const [queuedCallCount, setQueuedCallCount] = useState(0)
  const [lastQueuedCallPreview, setLastQueuedCallPreview] = useState(null)
  const closeTimerRef = useRef(null)
  const eventSourceRef = useRef(null)
  const reconnectTimerRef = useRef(null)
  const staleCheckTimerRef = useRef(null)
  const lastEventAtRef = useRef(0)
  const connectionOkRef = useRef(false)
  const isDialogOpenRef = useRef(false)
  const activeIncomingCallRef = useRef(null)
  const skipHistorySelectionApplyRef = useRef(false)
  const audioContextRef = useRef(null)
  const audioUnlockedRef = useRef(false)
  const autoCloseMs = Number(process.env.REACT_APP_CALLER_MODAL_AUTOCLOSE_MS || 20000)
  const callCategoryOptions = ["Customer", "Prospect", "Unlikely", "Irrelevant", "Spam"]

  const normalizeEditableCallerName = (value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : ""
    if (!trimmedValue) {
      return ""
    }

    const lowerValue = trimmedValue.toLowerCase()
    if (lowerValue === "unknown" || lowerValue === "unknown name") {
      return ""
    }

    return trimmedValue
  }

  const normalizeCallerNotes = (notes) => {
    if (!Array.isArray(notes)) {
      return []
    }

    return notes
      .map((note) => {
        const text = typeof note?.text === "string" ? note.text.trim() : ""
        if (!text) {
          return null
        }

        const createdAt = note?.createdAt || null
        const updatedAt = note?.updatedAt || createdAt

        return {
          id: note?.id || note?._id || null,
          text,
          createdAt,
          updatedAt,
        }
      })
      .filter(Boolean)
      .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
  }

  const formatNoteTimestamp = (value) => {
    if (!value) {
      return ""
    }

    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) {
      return ""
    }

    return `${formatAUDate(parsed, { weekday: false })} ${formatAUTime(parsed)}`
  }

  const mapCallerPayloadToDialogState = (payload) => {
    const callerNotes = normalizeCallerNotes(payload?.callerNotes)
    const fallbackNoteText = typeof payload?.callerNote === "string" ? payload.callerNote.trim() : ""
    const effectiveCallerNotes =
      callerNotes.length > 0
        ? callerNotes
        : fallbackNoteText
        ? [
            {
              id: null,
              text: fallbackNoteText,
              createdAt: payload?.timestamp || new Date().toISOString(),
              updatedAt: payload?.timestamp || new Date().toISOString(),
            },
          ]
        : []
    const latestNote = effectiveCallerNotes.length > 0 ? effectiveCallerNotes[0].text : null
    const callerName = normalizeEditableCallerName(payload?.profileCallerName)
    const customerName = typeof payload?.customerName === "string" ? payload.customerName.trim() : ""
    const customerMatched = Boolean(payload?.customerFound)
    const customerLabel = customerMatched
      ? customerName || "Matched customer"
      : callerName || "Unknown"

    return {
      callerName,
      callerNumber: payload?.callerNumber || "Unknown",
      customerLabel,
      customerFound: Boolean(payload?.customerFound),
      customerId: payload?.customerId || null,
      lastCallMessage: payload?.lastCallMessage || null,
      lastCallReceivedAt: payload?.lastCallReceivedAt || null,
      callCategory: payload?.callCategory || (payload?.isSpam ? "Spam" : null),
      isSpam: payload?.callCategory ? payload.callCategory === "Spam" : Boolean(payload?.isSpam),
      callerNote: latestNote,
      callerNotes: effectiveCallerNotes,
      timestamp: payload?.timestamp || null,
    }
  }

  const getActiveCallerNumberDigits = useCallback(() => {
    return String(incomingCall?.callerNumber || "").replace(/\D/g, "")
  }, [incomingCall?.callerNumber])

  const applyNumberMeta = useCallback(
    (meta) => {
      const nextCategory = meta?.callCategory || (meta?.isSpam ? "Spam" : null)
      const nextIsSpam = nextCategory ? nextCategory === "Spam" : Boolean(meta?.isSpam)
      const nextNote = meta?.callerNote || null
      const nextNotes = normalizeCallerNotes(meta?.callerNotes)
      const nextCallerName = normalizeEditableCallerName(meta?.profileCallerName || meta?.callerName)

      setIncomingCall((prev) => {
        if (!prev) {
          return prev
        }

        const nextCustomerLabel = prev?.customerFound
          ? prev?.customerLabel || "Matched customer"
          : nextCallerName
          ? nextCallerName
          : "Unknown"

        return {
          ...prev,
          callCategory: nextCategory,
          isSpam: nextIsSpam,
          callerNote: nextNotes.length > 0 ? nextNotes[0].text : nextNote,
          callerNotes: nextNotes,
          callerName: nextCallerName,
          customerLabel: nextCustomerLabel,
        }
      })

      setCallerNameDraft(nextCallerName)

      setCallHistory((prev) => {
        if (!prev.length || callHistoryIndex < 0 || callHistoryIndex >= prev.length) {
          return prev
        }

        return prev.map((entry, index) => {
          if (index !== callHistoryIndex) {
            return entry
          }

          return {
            ...entry,
            callCategory: nextCategory,
            isSpam: nextIsSpam,
            callerNote: nextNotes.length > 0 ? nextNotes[0].text : nextNote,
            callerNotes: nextNotes,
            profileCallerName: nextCallerName || null,
            callerName: nextCallerName,
          }
        })
      })

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("caller-meta-updated", {
            detail: {
              callerNumber: meta?.callerNumber || null,
              callCategory: nextCategory,
              isSpam: nextIsSpam,
              callerNote: nextNote,
              callerName: nextCallerName || null,
            },
          })
        )
      }
    },
    [callHistoryIndex]
  )

  const handleSelectCallCategory = useCallback(async (selectedCategory) => {
    const callerNumber = getActiveCallerNumberDigits()
    if (!callerNumber) {
      return
    }

    const normalizedCategory = typeof selectedCategory === "string" ? selectedCategory.trim() : null
    const nextCategory = incomingCall?.callCategory === normalizedCategory ? null : normalizedCategory

    setCategoryLoading(true)
    try {
      const { data } = await axios.put("/api/caller-id/number-meta/category", {
        callerNumber,
        callCategory: nextCategory,
      })

      applyNumberMeta(data)
    } catch (error) {
      console.error("Failed to update call category:", error)
    } finally {
      setCategoryLoading(false)
    }
  }, [applyNumberMeta, getActiveCallerNumberDigits, incomingCall?.callCategory])

  const getCategoryColorScheme = (category) => {
    if (category === "Spam") {
      return "red"
    }

    if (category === "Prospect") {
      return "green"
    }

    if (category === "Irrelevant") {
      return "orange"
    }

    if (category === "Unlikely") {
      return "yellow"
    }

    if (category === "Customer") {
      return "blue"
    }

    return "gray"
  }

  const handleOpenNoteEditor = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }

    const normalizedCallerName = normalizeEditableCallerName(incomingCall?.callerName)
    const normalizedNotes = normalizeCallerNotes(incomingCall?.callerNotes)

    setIsAutoClosePaused(true)
    setCallerNameDraft(normalizedCallerName)
    setInitialCallerName(normalizedCallerName)
    setDraftNotes(normalizedNotes)
    setInitialNotes(normalizedNotes)
    setNoteDraft("")
    setEditingNoteId(null)
    setIsNoteEditorOpen(true)
  }, [incomingCall?.callerName, incomingCall?.callerNotes])

  const handleCloseNoteEditor = useCallback(() => {
    setDraftNotes([])
    setInitialNotes([])
    setInitialCallerName("")
    setNoteDraft("")
    setEditingNoteId(null)
    setIsNoteEditorOpen(false)
  }, [])

  const callPersistNotesApi = useCallback(async ({ action, noteId, note }) => {
    const callerNumber = getActiveCallerNumberDigits()
    if (!callerNumber) {
      return null
    }

    const { data } = await axios.put("/api/caller-id/number-meta/note", {
      callerNumber,
      callerName: callerNameDraft,
      action,
      note,
      noteId,
    })

    return data
  }, [callerNameDraft, getActiveCallerNumberDigits])

  const handleAddNote = useCallback(() => {
    const trimmedNote = typeof noteDraft === "string" ? noteDraft.trim() : ""
    if (!trimmedNote) {
      return
    }

    const nowIso = new Date().toISOString()
    setDraftNotes((prev) => [
      {
        id: null,
        text: trimmedNote,
        createdAt: nowIso,
        updatedAt: nowIso,
      },
      ...prev,
    ])
    setNoteDraft("")
    setEditingNoteId(null)
  }, [noteDraft])

  const handleStartEditNote = useCallback((note) => {
    if (!note) {
      return
    }

    setEditingNoteId(note.id || `draft-${note.createdAt || Date.now()}`)
    setNoteDraft(note.text || "")
  }, [])

  const handleSaveEditedNote = useCallback(() => {
    if (!editingNoteId) {
      return
    }

    const trimmedNote = typeof noteDraft === "string" ? noteDraft.trim() : ""
    if (!trimmedNote) {
      return
    }

    setDraftNotes((prev) =>
      prev.map((item) => {
        const itemEditId = item.id || `draft-${item.createdAt || ""}`
        if (itemEditId !== editingNoteId) {
          return item
        }

        return {
          ...item,
          text: trimmedNote,
          updatedAt: new Date().toISOString(),
        }
      })
    )

    setEditingNoteId(null)
    setNoteDraft("")
  }, [editingNoteId, noteDraft])

  const handleDeleteNote = useCallback((noteItem) => {
    if (!noteItem) {
      return
    }

    const confirmed = window.confirm("Delete this note?")
    if (!confirmed) {
      return
    }

    const targetEditId = noteItem.id || `draft-${noteItem.createdAt || ""}`
    setDraftNotes((prev) =>
      prev.filter((item) => {
        const itemEditId = item.id || `draft-${item.createdAt || ""}`
        return itemEditId !== targetEditId
      })
    )

    if (editingNoteId === targetEditId) {
      setEditingNoteId(null)
      setNoteDraft("")
    }
  }, [editingNoteId])

  const handleStopEditingNote = useCallback(() => {
    setEditingNoteId(null)
    setNoteDraft("")
  }, [])

  const handleSaveNoteDialog = useCallback(async () => {
    setNoteLoading(true)

    try {
      const initialById = new Map(initialNotes.filter((note) => note?.id).map((note) => [note.id, note]))
      const draftById = new Map(draftNotes.filter((note) => note?.id).map((note) => [note.id, note]))

      const deletedNotes = initialNotes.filter((note) => note?.id && !draftById.has(note.id))
      const editedNotes = draftNotes.filter((note) => {
        if (!note?.id || !initialById.has(note.id)) {
          return false
        }

        const original = initialById.get(note.id)
        return String(original?.text || "").trim() !== String(note?.text || "").trim()
      })
      const addedNotes = draftNotes.filter((note) => !note?.id && String(note?.text || "").trim())
      const nameChanged = normalizeEditableCallerName(callerNameDraft) !== normalizeEditableCallerName(initialCallerName)

      if (deletedNotes.length === 0 && editedNotes.length === 0 && addedNotes.length === 0 && !nameChanged) {
        handleCloseNoteEditor()
        return
      }

      let latestResponse = null

      for (const note of deletedNotes) {
        latestResponse = await callPersistNotesApi({ action: "delete", noteId: note.id })
      }

      for (const note of editedNotes) {
        latestResponse = await callPersistNotesApi({
          action: "edit",
          noteId: note.id,
          note: String(note?.text || "").trim(),
        })
      }

      for (const note of addedNotes) {
        latestResponse = await callPersistNotesApi({
          action: "add",
          note: String(note?.text || "").trim(),
        })
      }

      if (nameChanged) {
        latestResponse = await callPersistNotesApi({ action: "setname" })
      }

      if (latestResponse) {
        applyNumberMeta(latestResponse)
      }

      handleCloseNoteEditor()
    } catch (error) {
      console.error("Failed to save caller notes dialog:", error)
    } finally {
      setNoteLoading(false)
    }
  }, [
    applyNumberMeta,
    callPersistNotesApi,
    callerNameDraft,
    draftNotes,
    handleCloseNoteEditor,
    initialCallerName,
    initialNotes,
  ])

  const emitConnectionStatus = (connected, reason) => {
    connectionOkRef.current = connected

    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("caller-sse-status", {
          detail: {
            connected,
            checkedAt: new Date().toISOString(),
            reason,
          },
        })
      )
    }
  }

  const applyCallHistorySelection = useCallback((historyEntry) => {
    if (!historyEntry) {
      return
    }

    setIncomingCall(mapCallerPayloadToDialogState(historyEntry))
  }, [])

  const loadGlobalCallerHistory = useCallback(async (options = {}) => {
    const preferNewest = Boolean(options?.preferNewest)
    const preserveCurrentSelection = Boolean(options?.preserveCurrentSelection)
    const selectedHistoryId = String(options?.selectedHistoryId || "").trim()
    const selectedCallerNumber = String(options?.selectedCallerNumber || incomingCall?.callerNumber || "").replace(/\D/g, "")
    const selectedTimestamp = options?.selectedTimestamp || incomingCall?.timestamp || null

    setCallHistoryLoading(true)
    try {
      const { data } = await axios.get("/api/caller-id/history?limit=30")
      const history = data?.history || []
      setCallHistory(history)

      if (history.length > 0) {
        if (preserveCurrentSelection && !preferNewest) {
          const matchingHistoryIdIndex = selectedHistoryId
            ? history.findIndex((entry) => String(entry?.historyId || entry?._id || "") === selectedHistoryId)
            : -1

          const matchingEntryIndex = history.findIndex((entry) => {
            const entryNumber = String(entry?.callerNumber || "").replace(/\D/g, "")
            return entryNumber === selectedCallerNumber && entry?.timestamp === selectedTimestamp
          })

          const fallbackNumberMatchIndex =
            matchingHistoryIdIndex >= 0
              ? matchingHistoryIdIndex
              : matchingEntryIndex >= 0
              ? matchingEntryIndex
              : history.findIndex((entry) => String(entry?.callerNumber || "").replace(/\D/g, "") === selectedCallerNumber)

          const nextIndex =
            fallbackNumberMatchIndex >= 0
              ? fallbackNumberMatchIndex
              : Math.min(callHistoryIndex, history.length - 1)

          skipHistorySelectionApplyRef.current = true
          setCallHistoryIndex(nextIndex)
          return
        }

        setCallHistoryIndex(0)
        applyCallHistorySelection(history[0])
      }
    } catch (error) {
      if (error?.response?.status !== 404) {
        console.error("Failed to load caller history:", error)
      }
      setCallHistory([])
      setCallHistoryIndex(0)
    } finally {
      setCallHistoryLoading(false)
    }
  }, [applyCallHistorySelection, callHistoryIndex, incomingCall?.callerNumber, incomingCall?.timestamp])

  const ensureAudioContext = useCallback(() => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext
    if (!AudioCtx) {
      return null
    }

    if (!audioContextRef.current || audioContextRef.current.state === "closed") {
      audioContextRef.current = new AudioCtx()
    }

    return audioContextRef.current
  }, [])

  const unlockAudioForIOS = useCallback(async () => {
    try {
      const context = ensureAudioContext()
      if (!context) {
        return
      }

      if (context.state === "suspended") {
        await context.resume()
      }

      // Tiny near-silent pulse to fully unlock output on iOS Safari.
      const oscillator = context.createOscillator()
      const gainNode = context.createGain()
      gainNode.gain.setValueAtTime(0.0001, context.currentTime)
      oscillator.frequency.setValueAtTime(440, context.currentTime)
      oscillator.connect(gainNode)
      gainNode.connect(context.destination)
      oscillator.start(context.currentTime)
      oscillator.stop(context.currentTime + 0.01)

      audioUnlockedRef.current = true
    } catch (error) {
      console.error("Failed to unlock caller ID audio context:", error)
    }
  }, [ensureAudioContext])

  const playIncomingCallSound = useCallback(async () => {
    try {
      const context = ensureAudioContext()
      if (!context) {
        return
      }

      if (context.state === "suspended") {
        try {
          await context.resume()
          audioUnlockedRef.current = true
        } catch (resumeError) {
          // iOS can reject resume without user interaction; keep silent fallback.
          console.warn("Caller ID audio context is still suspended:", resumeError)
          return
        }
      }

      const playTone = (startAt, duration, frequency, volume = 0.22) => {
        const oscillator = context.createOscillator()
        const gainNode = context.createGain()

        oscillator.type = "triangle"
        oscillator.frequency.setValueAtTime(frequency, startAt)

        gainNode.gain.setValueAtTime(0.001, startAt)
        gainNode.gain.exponentialRampToValueAtTime(volume, startAt + 0.015)
        gainNode.gain.exponentialRampToValueAtTime(0.001, startAt + duration)

        oscillator.connect(gainNode)
        gainNode.connect(context.destination)

        oscillator.start(startAt)
        oscillator.stop(startAt + duration)
      }

      const now = context.currentTime
      const ringDuration = 0.875
      const ringGap = 0.24
      const clusterGap = 0.45

      for (let ringIndex = 0; ringIndex < 3; ringIndex += 1) {
        const ringStart = now + ringIndex * (ringDuration + ringGap + clusterGap)
        playTone(ringStart, ringDuration, 700, 0.26)
        playTone(ringStart + 0.02, ringDuration, 960, 0.14)
      }
    } catch (error) {
      // Audio can be blocked by browser policy; modal still opens.
      console.error("Failed to play incoming call sound:", error)
    }
  }, [ensureAudioContext])

  const handleModalClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
    setIsAutoClosePaused(false)
    setIsNoteEditorOpen(false)
    setQueuedCallCount(0)
    setLastQueuedCallPreview(null)
    onClose()
  }

  const pauseAutoCloseOnInteraction = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }

    if (!isAutoClosePaused) {
      setIsAutoClosePaused(true)
    }
  }, [isAutoClosePaused])

  useEffect(() => {
    isDialogOpenRef.current = isOpen
  }, [isOpen])

  useEffect(() => {
    activeIncomingCallRef.current = incomingCall
  }, [incomingCall])

  useEffect(() => {
    const tryUnlock = () => {
      if (audioUnlockedRef.current) {
        return
      }

      unlockAudioForIOS()
    }

    const events = ["touchstart", "touchend", "pointerdown", "mousedown", "keydown"]
    events.forEach((eventName) => {
      window.addEventListener(eventName, tryUnlock, { passive: true })
    })

    return () => {
      events.forEach((eventName) => {
        window.removeEventListener(eventName, tryUnlock)
      })

      if (audioContextRef.current && audioContextRef.current.state !== "closed") {
        audioContextRef.current.close().catch(() => {})
      }
    }
  }, [unlockAudioForIOS])

  useEffect(() => {
    if (!userInfo?.token) {
      return
    }

    const reconnectDelayMs = Number(process.env.REACT_APP_CALLER_SSE_RECONNECT_MS || 3000)
    const staleAfterMs = Number(process.env.REACT_APP_CALLER_SSE_STALE_MS || 90000)
    const staleCheckIntervalMs = Number(process.env.REACT_APP_CALLER_SSE_CHECK_MS || 15000)
    const apiBaseUrl = getApiBaseUrl()
    const streamPath = "/api/caller-id/stream"
    const streamUrl = `${apiBaseUrl || ""}${streamPath}?token=${encodeURIComponent(userInfo.token)}`
    let disposed = false

    const markEventActivity = () => {
      lastEventAtRef.current = Date.now()
    }

    const clearReconnectTimer = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
    }

    const closeEventSource = () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
    }

    const scheduleReconnect = () => {
      if (disposed || reconnectTimerRef.current) {
        return
      }

      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null
        connectStream()
      }, reconnectDelayMs)
    }

    const connectStream = () => {
      if (disposed) {
        return
      }

      closeEventSource()
      markEventActivity()

      const eventSource = new EventSource(streamUrl)
      eventSourceRef.current = eventSource

      eventSource.onopen = () => {
        markEventActivity()
        clearReconnectTimer()
        emitConnectionStatus(true, "open")
      }

      eventSource.onerror = () => {
        emitConnectionStatus(false, "error")
        scheduleReconnect()
      }

      eventSource.addEventListener("connected", () => {
        markEventActivity()
        emitConnectionStatus(true, "connected-event")
      })

      eventSource.addEventListener("ping", () => {
        markEventActivity()
        emitConnectionStatus(true, "ping")
      })

      eventSource.addEventListener("caller-id", (event) => {
        markEventActivity()
        emitConnectionStatus(true, "caller-id")
        try {
          const payload = JSON.parse(event.data)
          const nextCall = mapCallerPayloadToDialogState(payload)
          const isDialogBusy = isDialogOpenRef.current && Boolean(activeIncomingCallRef.current)

          if (isDialogBusy) {
            setQueuedCallCount((prev) => prev + 1)
            setLastQueuedCallPreview({
              callerNumber: nextCall.callerNumber,
              timestamp: nextCall.timestamp,
            })
            setIsAutoClosePaused(true)
            loadGlobalCallerHistory({ preserveCurrentSelection: true })
            playIncomingCallSound()
            return
          }

          setIncomingCall(nextCall)
          setQueuedCallCount(0)
          setLastQueuedCallPreview(null)
          setIsAutoClosePaused(false)
          loadGlobalCallerHistory({ preferNewest: true })
          playIncomingCallSound()
          onOpen()
        } catch (error) {
          console.error("Failed to parse caller ID stream payload:", error)
        }
      })
    }

    const ensureStreamFreshness = () => {
      const timeSinceLastEvent = Date.now() - lastEventAtRef.current
      const streamState = eventSourceRef.current?.readyState
      const hasStaleConnection = timeSinceLastEvent > staleAfterMs
      const isClosed = streamState === EventSource.CLOSED
      const isOpen = streamState === EventSource.OPEN

      if (hasStaleConnection || isClosed) {
        emitConnectionStatus(false, hasStaleConnection ? "stale" : "closed")
        scheduleReconnect()
        return
      }

      emitConnectionStatus(Boolean(isOpen), "health-check")
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        ensureStreamFreshness()
      }
    }

    connectStream()

    staleCheckTimerRef.current = setInterval(() => {
      ensureStreamFreshness()
    }, staleCheckIntervalMs)

    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      disposed = true
      emitConnectionStatus(false, "cleanup")
      clearReconnectTimer()
      if (staleCheckTimerRef.current) {
        clearInterval(staleCheckTimerRef.current)
        staleCheckTimerRef.current = null
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      closeEventSource()
    }
  }, [loadGlobalCallerHistory, onOpen, playIncomingCallSound, userInfo?.token])

  useEffect(() => {
    const handleOpenLastCallerDialog = () => {
      if (isOpen) {
        return
      }

      const fetchLastCaller = async () => {
        try {
          await loadGlobalCallerHistory({ preferNewest: true })
          setIsAutoClosePaused(false)
          onOpen()
        } catch (error) {
          if (error?.response?.status !== 404) {
            console.error("Failed to fetch last caller info:", error)
          }
        }
      }

      fetchLastCaller()
    }

    window.addEventListener("caller-dialog-open-last", handleOpenLastCallerDialog)

    return () => {
      window.removeEventListener("caller-dialog-open-last", handleOpenLastCallerDialog)
    }
  }, [isOpen, loadGlobalCallerHistory, onOpen])

  useEffect(() => {
    const handleOpenCallerDialogFromHistory = (event) => {
      const payload = event?.detail
      const callerNumber = String(payload?.callerNumber || "").replace(/\D/g, "")

      if (!callerNumber) {
        return
      }

      const nextCall = mapCallerPayloadToDialogState(payload)
      setIncomingCall(nextCall)
      setIsAutoClosePaused(true)
      setQueuedCallCount(0)
      setLastQueuedCallPreview(null)
      onOpen()

      loadGlobalCallerHistory({
        preserveCurrentSelection: true,
        selectedHistoryId: payload?.historyId,
        selectedCallerNumber: payload?.callerNumber,
        selectedTimestamp: payload?.timestamp,
      })
    }

    window.addEventListener("caller-dialog-open-entry", handleOpenCallerDialogFromHistory)

    return () => {
      window.removeEventListener("caller-dialog-open-entry", handleOpenCallerDialogFromHistory)
    }
  }, [loadGlobalCallerHistory, onOpen])

  useEffect(() => {
    if (callHistory.length === 0) {
      return
    }

    if (skipHistorySelectionApplyRef.current) {
      skipHistorySelectionApplyRef.current = false
      return
    }

    const selectedHistoryEntry = callHistory[callHistoryIndex]
    applyCallHistorySelection(selectedHistoryEntry)
  }, [applyCallHistorySelection, callHistory, callHistoryIndex])

  useEffect(() => {
    if (callHistoryIndex === 0 && queuedCallCount > 0) {
      setQueuedCallCount(0)
      setLastQueuedCallPreview(null)
    }
  }, [callHistoryIndex, queuedCallCount])

  useEffect(() => {
    if (!isOpen || !autoCloseMs || autoCloseMs <= 0 || isAutoClosePaused) {
      return
    }

    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
    }

    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null
      onClose()
    }, autoCloseMs)

    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }
    }
  }, [autoCloseMs, isAutoClosePaused, isOpen, incomingCall, onClose])

  useEffect(() => {
    const fetchCustomerJobs = async () => {
      if (!isOpen || !incomingCall?.customerFound || !incomingCall?.customerId) {
        setCustomerJobs([])
        setHistoryLoading(false)
        return
      }

      setHistoryLoading(true)
      try {
        const { data } = await axios.get(`/api/jobs/customer/${incomingCall.customerId}`)
        setCustomerJobs(data.jobs || [])
      } catch (error) {
        console.error("Failed to load customer history for incoming call:", error)
        setCustomerJobs([])
      } finally {
        setHistoryLoading(false)
      }
    }

    fetchCustomerJobs()
  }, [incomingCall?.customerFound, incomingCall?.customerId, isOpen])

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
      }
    }
  }, [])

  const historyBg = mode("gray.50", "gray.700")
  const historyTextColor = mode("gray.600", "gray.300")
  const lastCallHighlightBg = mode("yellow.100", "yellow.700")
  const lastCallHighlightBorder = mode("yellow.300", "yellow.500")
  const lastCallHighlightText = mode("yellow.900", "yellow.100")
  const queuedCallBg = mode("orange.100", "orange.900")
  const queuedCallBorder = mode("orange.300", "orange.600")
  const queuedCallText = mode("orange.900", "orange.100")
  const customerCategoryBg = mode("blue.100", "blue.700")
  const customerCategoryHoverBg = mode("blue.200", "blue.600")
  const customerCategoryBorder = mode("blue.300", "blue.500")
  const customerCategoryText = mode("blue.900", "white")

  const formatCallerNumber = (value) => {
    const raw = String(value || "")
    const digitsOnly = raw.replace(/\D/g, "")

    if (digitsOnly.length !== 10) {
      return raw || "Unknown"
    }

    if (digitsOnly.startsWith("04")) {
      return `${digitsOnly.slice(0, 4)}-${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7, 10)}`
    }

    return `${digitsOnly.slice(0, 2)}-${digitsOnly.slice(2, 6)}-${digitsOnly.slice(6, 10)}`
  }

  const formatBusinessDayKey = (value) =>
    new Intl.DateTimeFormat("en-CA", {
      timeZone: getBusinessTimeZone(),
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date(value))

  const formatRelativeTimestamp = (dateValue) => {
    if (!dateValue) {
      return null
    }

    const inputDate = new Date(dateValue)
    if (Number.isNaN(inputDate.getTime())) {
      return null
    }

    const now = new Date()
    const yesterday = new Date(now)
    yesterday.setDate(yesterday.getDate() - 1)

    const inputDayKey = formatBusinessDayKey(inputDate)
    const todayKey = formatBusinessDayKey(now)
    const yesterdayKey = formatBusinessDayKey(yesterday)
    const timePart = formatAUTime(inputDate)

    if (inputDayKey === todayKey) {
      return `Today at ${timePart}`
    }

    if (inputDayKey === yesterdayKey) {
      return `Yesterday at ${timePart}`
    }

    const datePart = formatAUDate(inputDate, { weekday: false })
    return `On ${datePart} at ${timePart}`
  }

  const formatLastCallLabel = () => {
    if (!incomingCall?.lastCallReceivedAt) {
      return "No previous calls recorded"
    }

    const formatted = formatRelativeTimestamp(incomingCall.lastCallReceivedAt)
    return formatted ? `Last call received: ${formatted}` : (incomingCall?.lastCallMessage || "No previous calls recorded")
  }

  const hasPreviousCallForNumber = Boolean(incomingCall?.lastCallReceivedAt)

  const copyTextToClipboard = async (textValue) => {
    if (!textValue) {
      return false
    }

    const normalizedValue = String(textValue)

    if (navigator?.clipboard?.writeText && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(normalizedValue)
        return true
      } catch (error) {
        console.warn("Clipboard API failed, falling back to execCommand", error)
      }
    }

    try {
      const tempInput = document.createElement("textarea")
      tempInput.value = normalizedValue
      tempInput.setAttribute("readonly", "")
      tempInput.setAttribute("aria-hidden", "true")
      tempInput.style.position = "fixed"
      tempInput.style.top = "-9999px"
      tempInput.style.left = "-9999px"
      tempInput.style.opacity = "0"
      tempInput.style.width = "1px"
      tempInput.style.height = "1px"
      document.body.appendChild(tempInput)

      return await new Promise((resolve) => {
        window.setTimeout(() => {
          try {
            tempInput.focus()
            tempInput.select()
            tempInput.setSelectionRange(0, tempInput.value.length)
            const copied = document.execCommand("copy")
            document.body.removeChild(tempInput)
            resolve(copied)
          } catch (error) {
            document.body.removeChild(tempInput)
            console.error("Failed to copy caller number:", error)
            resolve(false)
          }
        }, 0)
      })
    } catch (error) {
      console.error("Failed to copy caller number:", error)
      return false
    }
  }

  const handleCopyCallerNumber = async () => {
    pauseAutoCloseOnInteraction()
    const rawDigits = String(incomingCall?.callerNumber || "").replace(/\D/g, "")
    const copied = await copyTextToClipboard(rawDigits)

    if (copied) {
      toast({
        description: "Number copied to clipboard",
        status: "success",
        duration: 1500,
        isClosable: true,
        position: "top",
      })
      return
    }

    toast({
      description: "Could not copy number",
      status: "warning",
      duration: 1800,
      isClosable: true,
      position: "top",
    })
  }

  return (
    <>
    <Modal isOpen={isOpen} onClose={handleModalClose} isCentered size='2xl'>
      <ModalOverlay />
      <ModalContent
        maxH='90vh'
        tabIndex={0}
        onMouseDown={pauseAutoCloseOnInteraction}
        onWheel={pauseAutoCloseOnInteraction}
        onTouchStart={pauseAutoCloseOnInteraction}
        onKeyDown={pauseAutoCloseOnInteraction}
        onScrollCapture={pauseAutoCloseOnInteraction}
      >
        <ModalHeader display='flex' justifyContent='center' alignItems='center' gap='4' fontSize='3xl' fontWeight='black'>
          <Text as='span'>Incoming Call</Text>
          {incomingCall?.timestamp && (
            <Text as='span' fontSize='sm' fontWeight='semibold' color={historyTextColor} mt={1}> 
              ({formatRelativeTimestamp(incomingCall.timestamp) || "Unknown time"})
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing='2' align='stretch'>
            {queuedCallCount > 0 && (
              <Box
                p='2'
                borderRadius='md'
                bg={queuedCallBg}
                border='1px solid'
                borderColor={queuedCallBorder}
              >
                <Text fontSize='sm' textAlign='center' color={queuedCallText} fontWeight='bold'>
                  {queuedCallCount} new call{queuedCallCount > 1 ? "s" : ""} waiting. Current call details are locked until you switch.
                </Text>
                {lastQueuedCallPreview?.callerNumber && (
                  <Text mt='1' fontSize='xs' textAlign='center' color={queuedCallText}>
                    Latest waiting number: {formatCallerNumber(lastQueuedCallPreview.callerNumber)}
                  </Text>
                )}
              </Box>
            )}
            <Box borderWidth='1px' borderRadius='md' p='2'>
              <VStack spacing='2' align='stretch'>
                <HStack justifyContent='space-between' alignItems='start' spacing='3'>
                  <VStack align='start' spacing='0'>
                    <Text fontSize='xs' fontWeight='bold' letterSpacing='0.12em' textTransform='uppercase' color={historyTextColor}>
                      Caller Details
                    </Text>
                  </VStack>
                  <HStack spacing='2' flexShrink={0} alignItems='center'>
                    <Menu placement='bottom-end'>
                      <MenuButton
                        as={Button}
                        size='sm'
                        colorScheme={getCategoryColorScheme(incomingCall?.callCategory)}
                        variant={incomingCall?.callCategory ? "solid" : "outline"}
                        isLoading={categoryLoading}
                        loadingText='Saving'
                        onClick={pauseAutoCloseOnInteraction}
                        bg={incomingCall?.callCategory === "Customer" ? customerCategoryBg : undefined}
                        color={incomingCall?.callCategory === "Customer" ? customerCategoryText : undefined}
                        border='1px solid'
                        borderColor={incomingCall?.callCategory === "Customer" ? customerCategoryBorder : undefined}
                        _hover={
                          incomingCall?.callCategory === "Customer"
                            ? { bg: customerCategoryHoverBg }
                            : undefined
                        }
                      >
                        {incomingCall?.callCategory || "Category"}
                      </MenuButton>
                      <MenuList>
                        {callCategoryOptions.map((categoryName) => (
                          <MenuItem
                            key={categoryName}
                            fontWeight={incomingCall?.callCategory === categoryName ? "bold" : "normal"}
                            onClick={() => handleSelectCallCategory(categoryName)}
                          >
                            {categoryName}
                          </MenuItem>
                        ))}
                        <MenuItem onClick={() => handleSelectCallCategory(null)} isDisabled={!incomingCall?.callCategory}>
                          Remove category
                        </MenuItem>
                      </MenuList>
                    </Menu>
                    <Button
                      size='sm'
                      colorScheme='cyan'
                      variant='outline'
                      onClick={handleOpenNoteEditor}
                      isLoading={noteLoading}
                      loadingText='Opening'
                    >
                      Notes
                    </Button>
                  </HStack>
                </HStack>
                <HStack justifyContent='center' alignItems='center' spacing='2'>
                  {incomingCall?.isSpam && <WarningIcon color='red.500' boxSize={5} title='Spam number' />}
                  <Text fontSize='xl' textAlign='center'>
                    Number:{" "}
                    <Text
                      as='span'
                      fontWeight='bold'
                      cursor='pointer'
                      color='#1A365D'
                      _hover={{ color: "#153E75" }}
                      title='Click to copy raw number'
                      onClick={handleCopyCallerNumber}
                    >
                      {formatCallerNumber(incomingCall?.callerNumber)}
                    </Text>
                  </Text>
                  {incomingCall?.isSpam && <WarningIcon color='red.500' boxSize={5} title='Spam number' />}
                </HStack>
                <Text fontSize='lg' textAlign='center' lineHeight='short'>
                  {incomingCall?.customerFound ? "Customer" : "Caller name"}: <Text as='span' fontWeight='bold'>{incomingCall?.customerLabel || "Unknown"}</Text>
                </Text>
                {incomingCall?.callerNote && (
                  <Text
                    fontSize='sm'
                    textAlign='center'
                    color={historyTextColor}
                    lineHeight='short'
                    p='2'
                    borderRadius='md'
                    bg={mode("blue.50", "blue.900")}
                    border='1px solid'
                    borderColor={mode("blue.100", "blue.700")}
                  >
                    <HStack justifyContent='center' spacing='2' flexWrap='wrap'>
                      <Text as='span'>Note: {incomingCall.callerNote}</Text>
                      {Array.isArray(incomingCall?.callerNotes) && incomingCall.callerNotes.length > 1 && (
                        <HStack as='span' spacing='1' color={mode("blue.700", "blue.200")}>
                          <InfoOutlineIcon boxSize={3.5} />
                          <Text as='span' fontSize='xs' fontWeight='semibold'>
                            +{incomingCall.callerNotes.length - 1} more
                          </Text>
                        </HStack>
                      )}
                    </HStack>
                  </Text>
                )}
                {hasPreviousCallForNumber ? (
                  <Box
                    p='2'
                    borderRadius='md'
                    bg={lastCallHighlightBg}
                    border='1px solid'
                    borderColor={lastCallHighlightBorder}
                  >
                    <Text fontSize='md' textAlign='center' color={lastCallHighlightText} fontWeight='semibold' lineHeight='short'>
                      {formatLastCallLabel()}
                    </Text>
                  </Box>
                ) : (
                  <Text fontSize='md' textAlign='center' color={historyTextColor} lineHeight='short'>
                    {formatLastCallLabel()}
                  </Text>
                )}
                <HStack justifyContent='space-between' alignItems='center' pt='0'>
                  <IconButton
                    aria-label='Older call history event'
                    icon={<ChevronLeftIcon boxSize={5} />}
                    size='sm'
                    colorScheme='cyan'
                    variant='outline'
                    onClick={() => {
                      pauseAutoCloseOnInteraction()
                      setCallHistoryIndex((prev) => Math.min(prev + 1, callHistory.length - 1))
                    }}
                    isDisabled={callHistoryLoading || callHistory.length <= 1 || callHistoryIndex >= callHistory.length - 1}
                  />
                  <Text fontSize='sm' color={historyTextColor} fontWeight='semibold'>
                    {callHistoryLoading
                      ? "Loading call history..."
                      : callHistory.length > 0
                      ? `${callHistoryIndex + 1} / ${callHistory.length}`
                      : "No call history events"}
                  </Text>
                  <IconButton
                    aria-label='Newer call history event'
                    icon={<ChevronRightIcon boxSize={5} />}
                    size='sm'
                    colorScheme='cyan'
                    variant='outline'
                    onClick={() => {
                      pauseAutoCloseOnInteraction()
                      setCallHistoryIndex((prev) => Math.max(prev - 1, 0))
                    }}
                    isDisabled={callHistoryLoading || callHistory.length <= 1 || callHistoryIndex <= 0}
                  />
                </HStack>
              </VStack>
            </Box>

            <Box borderWidth='1px' borderRadius='md' p='3'>
              <Text fontSize='lg' fontWeight='bold' mb='3'>
                Job History
              </Text>
              <Box maxH='45vh' overflowY='auto' pr='1'>
                {!incomingCall?.customerFound ? (
                  <Box bg={historyBg} p='4' borderRadius='md' textAlign='center'>
                    <Text color={historyTextColor}>Number not in database</Text>
                  </Box>
                ) : historyLoading ? (
                  <Box display='flex' justifyContent='center' alignItems='center' minH='180px'>
                    <Spinner size='lg' />
                  </Box>
                ) : customerJobs.length === 0 ? (
                  <Box bg={historyBg} p='4' borderRadius='md' textAlign='center'>
                    <Text color={historyTextColor}>No jobs found for this customer</Text>
                  </Box>
                ) : (
                  <VStack spacing='4' align='stretch'>
                    {customerJobs.map((job) => (
                      <JobCard
                        key={job.JOB_NO}
                        job={job}
                        loading={false}
                        listType='history'
                        onJobCardClick={handleModalClose}
                      />
                    ))}
                  </VStack>
                )}
              </Box>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter justifyContent='center'>
          <Button colorScheme='cyan' size='lg' onClick={handleModalClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>

    <Modal isOpen={isNoteEditorOpen} onClose={handleCloseNoteEditor} isCentered size='lg'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Notes</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize='sm' mb='2' color={historyTextColor}>
            Manage notes for {formatCallerNumber(incomingCall?.callerNumber)}
          </Text>
          <Text fontSize='sm' mb='1' color={historyTextColor}>
            Caller Name
          </Text>
          <Input
            value={callerNameDraft}
            onChange={(event) => setCallerNameDraft(event.target.value)}
            placeholder='Type caller name...'
            maxLength={120}
            mb='3'
          />

          <Box mb='4'>
            <Text fontSize='sm' mb='2' color={historyTextColor}>
              Notes list
            </Text>
            <VStack spacing='2' align='stretch'>
              {Array.isArray(draftNotes) && draftNotes.length > 0 ? (
                draftNotes.map((noteItem) => (
                  <Box key={noteItem.id || noteItem.createdAt || noteItem.text} borderWidth='1px' borderRadius='md' p='2'>
                    <HStack justifyContent='space-between' alignItems='start' spacing='2' mb='1'>
                      <Text fontSize='xs' color={historyTextColor}>
                        {formatNoteTimestamp(noteItem.updatedAt || noteItem.createdAt)}
                      </Text>
                      <HStack spacing='1'>
                        <Button size='xs' variant='outline' onClick={() => handleStartEditNote(noteItem)} isDisabled={noteLoading}>
                          Edit
                        </Button>
                        <Button size='xs' colorScheme='red' variant='outline' onClick={() => handleDeleteNote(noteItem)} isLoading={noteLoading}>
                          Delete
                        </Button>
                      </HStack>
                    </HStack>
                    <Text fontSize='sm' whiteSpace='pre-wrap'>
                      {noteItem.text}
                    </Text>
                  </Box>
                ))
              ) : (
                <Box borderWidth='1px' borderRadius='md' p='2'>
                  <Text fontSize='sm' color={historyTextColor}>No notes yet</Text>
                </Box>
              )}
            </VStack>
          </Box>

          <Text fontSize='sm' mb='1' color={historyTextColor}>
            {editingNoteId ? "Edit note" : "Add note"}
          </Text>
          <Textarea
            value={noteDraft}
            onChange={(event) => setNoteDraft(event.target.value)}
            placeholder='Type note or comment...'
            minH='110px'
            maxLength={300}
            resize='vertical'
          />
          <Text textAlign='right' fontSize='xs' color={historyTextColor} mt='1'>
            {noteDraft.length}/300
          </Text>
          <HStack justifyContent='flex-end' mt='2'>
            {editingNoteId ? (
              <>
                <Button variant='outline' onClick={handleStopEditingNote} isDisabled={noteLoading}>
                  Cancel Edit
                </Button>
                <Button colorScheme='cyan' onClick={handleSaveEditedNote} isDisabled={!noteDraft.trim() || noteLoading}>
                  Save Edit
                </Button>
              </>
            ) : (
              <Button colorScheme='cyan' onClick={handleAddNote} isDisabled={!noteDraft.trim() || noteLoading}>
                Add Note
              </Button>
            )}
          </HStack>
        </ModalBody>
        <ModalFooter>
          <Button variant='ghost' mr='3' onClick={handleCloseNoteEditor} isDisabled={noteLoading}>
            Cancel
          </Button>
          <Button colorScheme='cyan' onClick={handleSaveNoteDialog} isLoading={noteLoading} loadingText='Saving'>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
    </>
  )
}

export default CallerIdLiveListener
